'use client';

import React from 'react';
import { FaDiscord } from 'react-icons/fa6';
import Link from 'next/link';
import MatrixGrid from '@/app/components/MatrixGrid';

export default function ClipAndEarnPage() {
    return (
        <div className="mt-15 text-gray-200 min-h-screen overflow-hidden relative">
            <MatrixGrid />
            <div className="flex flex-col items-center justify-center space-y-8 relative z-10 max-w-7xl mx-auto px-4 py-16">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-6xl font-mono font-bold tracking-widest mb-4">
                        <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                            EARN WITH CLIPS
                        </span>
                    </h1>
                    <p className="uppercase text-green-300 font-mono text-sm md:text-base">
                        <span className="inline-block animate-pulse">{'['}</span>   Earn money by creating and posting clips from the <span className="text-green-400 font-bold">SokoGambles Kick channel</span> <span className="inline-block animate-pulse">{']'}</span>
                    </p>
                </div>

                {/* Payment Tiers */}
                <div className="w-full space-y-4">

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {[
                            { views: '10K', reward: '$25' },
                            { views: '25K', reward: '$50' },
                            { views: '50K', reward: '$100' },
                            { views: '100K', reward: '$150' },
                            { views: '250K', reward: '$250' },
                        ].map((tier, index) => (
                            <div
                                key={index}
                                className="bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-2xl overflow-hidden rounded-lg p-4 text-center"
                            >
                                <p className="text-3xl font-bold text-gray-200 mb-1">{tier.views}</p>
                                <p className="text-sm text-gray-200 mb-2">views</p>
                                <p className="text-2xl font-bold text-white">{tier.reward}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <h2 className="mt-10 mb-10 text-2xl md:text-4xl font-mono font-bold text-center mb-4 text-green-400">
                    <span className="inline-block animate-pulse">{'['}</span> INTRODUCTION <span className="inline-block animate-pulse">{']'}</span>
                </h2>

                {/* Requirements */}
                <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Requirements */}
                    <div className="backdrop-blur shadow-sm rounded-2xl overflow-hidden backdrop-blur border-b border-green-400/30 rounded-lg p-6">
                        <h3 className="text-xl font-mono font-bold text-green-400 mb-4 flex items-center gap-2">
                            REQUIREMENTS
                        </h3>
                        <ul className="space-y-2 text-gray-300 text-sm font-mono">
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Clips from <span className="text-green-400">kick.com/sokogambles</span> only</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Include &quot;kick.com/sokogambles&quot; watermark</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Post on YouTube or Instagram</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Minimum 10 seconds long</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Use hashtag <span className="text-green-400">#sokoclips</span></span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-green-400">▸</span>
                                <span>Legitimate views only (no bots)</span>
                            </li>
                        </ul>
                    </div>

                    {/* Rules */}
                    <div className="backdrop-blur shadow-sm rounded-2xl overflow-hidden backdrop-blur border-b border-green-400/30 rounded-lg p-6">
                        <h3 className="text-xl font-mono font-bold text-red-400 mb-4 flex items-center gap-2">
                            IMPORTANT RULES
                        </h3>
                        <ul className="space-y-2 text-gray-300 text-sm font-mono">
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>No TikTok (gambling content banned)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>No clips from sokoloco channel</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>No reposting others&apos; clips</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>No bought/bot views</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>One claim per clip only</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-400">✗</span>
                                <span>Don&apos;t spam or DM staff</span>
                            </li>
                        </ul>
                    </div>

                    {/* How to Claim */}
                    <div className="backdrop-blur shadow-sm rounded-2xl overflow-hidden backdrop-blur border-b border-green-400/30 rounded-lg p-6">
                        <h3 className="text-xl font-mono font-bold text-green-400 mb-4 flex items-center gap-2">
                            HOW TO CLAIM PAYMENT
                        </h3>
                        <ol className="space-y-3 text-gray-200 font-mono text-sm">
                            <li className="flex items-start gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-green-500 rounded-full text-white text-xs font-bold">1</span>
                                <span>Open a ticket in <span className="text-green-400">〔🎫〕open-ticket</span> channel on Discord</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-green-500 rounded-full text-white text-xs font-bold">2</span>
                                <span>Provide your clip link</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-green-500 rounded-full text-white text-xs font-bold">3</span>
                                <span>Include a screenshot of your clip stats</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 bg-green-500 rounded-full text-white text-xs font-bold">4</span>
                                <span>Provide your LTC wallet address</span>
                            </li>
                        </ol>
                    </div>
                </div>

                <div>
                    {/* CTA */}
                    <Link
                        href="https://discord.com/invite/sokoloco"
                        className="group flex items-center gap-3 px-4 bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 rounded-xl shadow-lg hover:shadow-emerald-500/30 hover:border-green-400 hover: transition-all duration-200 px-8 py-4 font-mono font-bold text-gray-200 relative overflow-hidden">
                        <FaDiscord className="w-4 h-4 lg:w-6 lg:h-6" />
                        <span className="font-mono font-semibold">JOIN DISCORD NOW</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}